import { cn } from "@/utils";
import { cva } from "class-variance-authority";
import { Check, Plus, Search, Slash } from "lucide-react";
import {
  createContext,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
  type CSSProperties,
  type DragEvent,
  type ReactNode,
  type RefObject,
} from "react";
import {
  CanvasToolbarColorSwatch,
  type CanvasSwatchRole,
} from "../../../nodes/shared/CanvasToolbarGraphics";
import {
  CANVAS_GLYPH_STROKE,
  CANVAS_MENU_GLYPH,
  CANVAS_TILE_GLYPH,
} from "../canvasToolbarComposition";

/**
 * The vocabulary that fills the tray — exp-canvas-toolbar tray-content.jsx,
 * in utilities. Eight primitives carry every fill: a titled band (a group
 * well, the bar's well inverted — n-05 on the n-00 panel), the swatch grid,
 * the tile grid (N equal columns on the 32px rung), the plate grid, the row
 * list, the app grid, the tab strip and the find-and-add form. The deck
 * scrolls under a pinned head or foot; `canvasTray.css` owns the measured
 * clearances. Nothing here moves: every transition is a colour step.
 *
 * Selection inside a tray is the bar's own rust tint on a tile (one tool,
 * one colour, in the two places it is described) and the neutral pressed
 * register on a row, an app or a tab.
 */

/**
 * Portrait: the frame beside the shed bar's vertical column, seven of the
 * bar's tiles wide (`--xtt-portrait-w` in canvasTray.css). The primitives
 * reflow for it rather than any tray forking: a one-row grid wraps into
 * balanced rows of at most seven cells, a multi-row grid caps at seven,
 * plates keep their landscape size at three a row, and a pair stacks. The
 * auto-fill grids (apps, emoji, stickers) reflow by width on their own.
 */
const TrayPortraitContext = createContext(false);
export const TrayPortraitProvider = TrayPortraitContext.Provider;
export function useTrayPortrait(): boolean {
  return useContext(TrayPortraitContext);
}

/** Cells a portrait row holds: the frame's seven tiles. */
export const TRAY_PORTRAIT_MAX_COLUMNS = 7;
/** Plates a portrait row holds at their landscape width. */
export const TRAY_PORTRAIT_PLATE_COLUMNS = 3;

/**
 * The columns a grid of `count` cells, drawn `columns` wide in landscape,
 * takes in portrait: a single row wraps into balanced rows (eleven
 * swatches are 6 + 5, not 7 + 4); a grid that already wraps keeps its
 * cells' size and drops to the cap.
 */
export function portraitColumns(
  columns: number,
  count: number,
  max = TRAY_PORTRAIT_MAX_COLUMNS,
): number {
  if (columns <= max) return columns;
  if (columns < count) return max;
  return Math.ceil(count / Math.ceil(count / max));
}

function useColumns(columns: number, count: number): number {
  return useTrayPortrait() ? portraitColumns(columns, count) : columns;
}

export const trayTileVariants = cva(
  [
    "relative inline-flex min-w-0 items-center justify-center p-0",
    "h-8 rounded-md border-0 bg-transparent text-n-70 cursor-pointer outline-none",
    "transition-[background-color,color,opacity] duration-150 ease-out",
    "hover:duration-100 hover:bg-n-10 hover:text-n-95",
    "active:duration-0 active:opacity-92",
    "disabled:text-n-30 disabled:cursor-default disabled:hover:bg-transparent",
    "focus-visible:outline-2 focus-visible:outline-solid focus-visible:outline-rust focus-visible:-outline-offset-2",
    "aria-pressed:bg-[color-mix(in_oklab,var(--rust)_12%,transparent)] aria-pressed:text-rust",
    "aria-pressed:hover:bg-[color-mix(in_oklab,var(--rust)_18%,transparent)]",
  ],
  {
    variants: {
      /** A specimen group may take the lg rung; a verb group may not. */
      rung: { sm: "", lg: "h-12 [&_svg]:size-7" },
    },
    defaultVariants: { rung: "sm" },
  },
);

const focusInset =
  "outline-none focus-visible:outline-2 focus-visible:outline-solid focus-visible:outline-rust focus-visible:-outline-offset-2";

export function Tray({ children }: { children: ReactNode }) {
  return (
    <div className="xtc relative flex w-full flex-col items-stretch font-sans text-n-90">
      {children}
    </div>
  );
}

export function TrayDeck({
  children,
  deckRef,
}: {
  children: ReactNode;
  deckRef?: RefObject<HTMLDivElement | null>;
}) {
  return (
    <div
      ref={deckRef}
      className="xtc-deck flex flex-none flex-col gap-2 overflow-x-hidden overflow-y-auto overscroll-contain p-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
    >
      {children}
    </div>
  );
}

/** Navigation, pinned at the top of the panel; the deck scrolls under it. */
export function TrayHead({ children }: { children: ReactNode }) {
  return (
    <div className="xtc-head absolute inset-x-0 top-0 z-[1] flex items-stretch gap-1 bg-n-00 px-2 py-0.5 dark:bg-n-20 [&>*]:h-10 [&>*]:min-w-0 [&>*]:flex-auto">
      {children}
    </div>
  );
}

/** The head's mirror at the panel's foot, carrying the 8px tuck in its padding. */
export function TrayFoot({ children }: { children: ReactNode }) {
  return (
    <div className="xtc-foot absolute inset-x-0 bottom-0 z-[1] flex items-stretch gap-1 bg-n-00 px-2 pt-0.5 dark:bg-n-20 [&>*]:h-10 [&>*]:min-w-0 [&>*]:flex-auto">
      {children}
    </div>
  );
}

interface TraySectionProps {
  /** Drawn only where it is wayfinding; a palette never names itself. */
  label?: string;
  ariaLabel?: string;
  /** Dimmed, never removed — a vanished band would reflow the measured deck. */
  inert?: boolean;
  /** Rows, apps and stickers are content, not a field of controls: no well. */
  bare?: boolean;
  children: ReactNode;
}

export function TraySection({
  label,
  ariaLabel,
  inert,
  bare,
  children,
}: TraySectionProps) {
  const id = useId();
  return (
    <div
      role="group"
      aria-labelledby={label ? id : undefined}
      aria-label={label ? undefined : ariaLabel}
      className={cn(
        "flex min-w-0 flex-col gap-1",
        !bare && "rounded-lg bg-n-05 p-0.5",
      )}
    >
      {label ? (
        <p
          id={id}
          className={cn(
            "m-0 text-sm leading-4 font-normal text-n-60",
            inert && "text-n-30",
          )}
        >
          {label}
        </p>
      ) : null}
      <div
        className={cn("contents", inert && "pointer-events-none opacity-45")}
      >
        {children}
      </div>
    </div>
  );
}

/** Two equal halves, stated as a grid so a palette inside has width to
 * divide; portrait has no width to divide, so they stack. */
export function TrayPair({ children }: { children: ReactNode }) {
  const portrait = useTrayPortrait();
  return (
    <div
      className={cn(
        "grid items-stretch gap-2",
        portrait ? "grid-cols-1" : "grid-cols-2",
      )}
    >
      {children}
    </div>
  );
}

/** Clusters on one row, each growing by its own cell count. */
export function TrayCluster({ children }: { children: ReactNode }) {
  return (
    <div className="flex min-w-0 items-center gap-2 [&>*]:min-w-0 [&>*]:flex-[var(--xtc-cols,1)_1_0]">
      {children}
    </div>
  );
}

export interface TrayTileItem {
  key: string;
  name: string;
  node: ReactNode;
  disabled?: boolean;
}

interface TileGridProps {
  items: TrayTileItem[];
  value?: string | null;
  onChange?: (key: string) => void;
  /** A set of toggles rather than a pick (bold · italic · …). */
  values?: Set<string>;
  onToggle?: (key: string) => void;
  columns?: number;
  rung?: "sm" | "lg";
}

const cols = (n: number): CSSProperties =>
  ({
    "--xtc-cols": String(n),
    gridTemplateColumns: `repeat(${n}, minmax(0, 1fr))`,
  }) as CSSProperties;

/** N equal columns of control cells; width from the column, height from the rung. */
export function TileGrid({
  items,
  value,
  onChange,
  values,
  onToggle,
  columns,
  rung = "sm",
}: TileGridProps) {
  const multi = values !== undefined;
  const selectable = value !== undefined || multi;
  const isOn = (k: string) => (multi ? values.has(k) : k === value);
  const n = useColumns(columns || items.length, items.length);
  return (
    <div
      className={cn("grid gap-0.5", rung === "lg" && "gap-1")}
      style={cols(n)}
    >
      {items.map((it) => (
        <button
          key={it.key}
          type="button"
          className={trayTileVariants({ rung })}
          aria-pressed={selectable ? isOn(it.key) : undefined}
          aria-label={it.name}
          title={it.name}
          disabled={it.disabled}
          onClick={() => (multi ? onToggle?.(it.key) : onChange?.(it.key))}
        >
          {it.node}
        </button>
      ))}
    </div>
  );
}

export interface TrayNewRowProps {
  /** The tool's placement is on the cursor. */
  armed: boolean;
  onSelect: () => void;
}

/** A popped tray's verb: one full-width row that arms the tool's placement,
 * pressed while it is armed. */
export function TrayNewRow({
  label,
  armed,
  onSelect,
}: TrayNewRowProps & { label: string }) {
  return (
    <TraySection ariaLabel={label}>
      <TileGrid
        items={[
          {
            key: "new",
            name: label,
            node: (
              <span className="inline-flex items-center gap-2 text-base leading-none">
                <Plus
                  size={CANVAS_TILE_GLYPH}
                  strokeWidth={CANVAS_GLYPH_STROKE}
                  aria-hidden="true"
                />
                {label}
              </span>
            ),
          },
        ]}
        value={armed ? "new" : null}
        onChange={onSelect}
        columns={1}
      />
    </TraySection>
  );
}

export interface TraySwatchItem {
  key: string;
  name: string;
  /** A fill row's tint; omit for the no-colour cell. */
  tint?: string;
  /** A mark or text row's mark. */
  mark?: string;
}

interface SwatchGridProps {
  items: TraySwatchItem[];
  value?: string | null;
  onChange?: (key: string) => void;
  columns?: number;
  disabled?: boolean;
  /**
   * What the row sets; the cell draws it the way the bars' swatch does
   * (D44): a fill row is the tinted cell it always was, a mark row draws
   * the ring, a text row the letter A.
   */
  role?: CanvasSwatchRole;
}

/** Colour. A 1.5px edge at every state; selected is the rust edge plus a Check. */
export function SwatchGrid({
  items,
  value,
  onChange,
  columns,
  disabled,
  role = "fill",
}: SwatchGridProps) {
  const drawsMark = role === "mark" || role === "text";
  const n = useColumns(columns || items.length, items.length);
  return (
    <div className="grid gap-0.5" style={cols(n)}>
      {items.map((it) => {
        const on = it.key === value;
        if (drawsMark) {
          return (
            <button
              key={it.key}
              type="button"
              className={cn(
                "grid h-8 min-w-0 cursor-pointer place-items-center rounded-sm border-[length:var(--border-2)] p-0 [&>*]:col-start-1 [&>*]:row-start-1",
                "border-[color-mix(in_oklab,var(--ink)_12%,transparent)] transition-[border-color,opacity] duration-150 ease-out",
                "hover:duration-100 hover:border-[color-mix(in_oklab,var(--ink)_28%,transparent)] active:opacity-92",
                "aria-pressed:border-rust aria-pressed:hover:border-[color-mix(in_oklab,var(--rust)_80%,var(--ink))]",
                "disabled:cursor-default",
                focusInset,
              )}
              aria-pressed={on}
              aria-label={it.name}
              title={it.name}
              disabled={disabled || undefined}
              onClick={() => onChange?.(it.key)}
            >
              {on && role === "text" ? null : (
                <CanvasToolbarColorSwatch role={role} mark={it.mark} />
              )}
              {on ? (
                <Check
                  size={CANVAS_MENU_GLYPH}
                  strokeWidth={CANVAS_GLYPH_STROKE}
                  style={{ color: it.mark }}
                  aria-hidden="true"
                />
              ) : null}
            </button>
          );
        }
        return (
          <button
            key={it.key}
            type="button"
            className={cn(
              "grid h-8 min-w-0 cursor-pointer place-items-center rounded-sm border-[length:var(--border-2)] p-0 text-n-60",
              "border-[color-mix(in_oklab,var(--ink)_12%,transparent)] transition-[border-color,opacity] duration-150 ease-out",
              "hover:duration-100 hover:border-[color-mix(in_oklab,var(--ink)_28%,transparent)] active:opacity-92",
              "aria-pressed:border-rust aria-pressed:hover:border-[color-mix(in_oklab,var(--rust)_80%,var(--ink))]",
              "disabled:cursor-default",
              focusInset,
            )}
            style={it.tint ? { background: it.tint } : undefined}
            aria-pressed={on}
            aria-label={it.name}
            title={it.name}
            disabled={disabled || undefined}
            onClick={() => onChange?.(it.key)}
          >
            {!it.tint ? (
              <Slash
                size={CANVAS_MENU_GLYPH}
                strokeWidth={CANVAS_GLYPH_STROKE}
                aria-hidden="true"
              />
            ) : null}
            {on ? (
              <Check
                className="text-ink"
                size={CANVAS_MENU_GLYPH}
                strokeWidth={CANVAS_GLYPH_STROKE}
                aria-hidden="true"
              />
            ) : null}
          </button>
        );
      })}
    </div>
  );
}

export interface TrayPlateItem {
  key: string;
  name: string;
  /** The preview inside the plate's frame. */
  wire: ReactNode;
  title?: string;
}

interface PlateGridProps {
  items: TrayPlateItem[];
  value?: string | null;
  onChange?: (key: string) => void;
  columns?: number;
  onDragStart?: (item: TrayPlateItem, e: DragEvent<HTMLElement>) => void;
}

/** A layout picked by looking at it: a framed preview over a caption. Not a
 * <button>: a preview may carry controls of its own (the timer's face), so
 * the plate is a focusable role=button and the preview is hidden from the
 * tree. */
export function PlateGrid({
  items,
  value,
  onChange,
  columns = 4,
  onDragStart,
}: PlateGridProps) {
  const selectable = value !== undefined;
  // Plates keep their size: the cap, never a balanced row of wider ones.
  const n = useTrayPortrait()
    ? Math.min(columns, TRAY_PORTRAIT_PLATE_COLUMNS)
    : columns;
  return (
    <div className="grid gap-x-2 gap-y-4" style={cols(n)}>
      {items.map((it) => (
        <div
          key={it.key}
          role="button"
          tabIndex={0}
          data-plate={it.key}
          className={cn(
            "group flex min-w-0 cursor-pointer flex-col gap-1 border-0 bg-transparent p-0 text-left active:opacity-92",
            onDragStart && "cursor-grab active:cursor-grabbing",
            focusInset,
          )}
          aria-pressed={selectable ? it.key === value : undefined}
          aria-label={it.name}
          title={it.title ?? it.name}
          draggable={onDragStart ? true : undefined}
          onDragStart={onDragStart ? (e) => onDragStart(it, e) : undefined}
          onClick={() => onChange?.(it.key)}
          onKeyDown={(e) => {
            if (e.key !== "Enter" && e.key !== " ") return;
            e.preventDefault();
            onChange?.(it.key);
          }}
        >
          <span
            className="block h-[calc(var(--size-16)-var(--space-1))] overflow-hidden rounded-lg border-[length:var(--border-2)] border-n-10 bg-n-05 transition-[border-color] duration-150 ease-out group-hover:duration-100 group-hover:border-n-30 group-aria-pressed:border-rust"
            aria-hidden="true"
          >
            {it.wire}
          </span>
          <span className="block truncate text-xs leading-5 font-normal text-n-70 transition-colors duration-150 group-hover:text-n-90 group-aria-pressed:font-medium group-aria-pressed:text-n-90">
            {it.name}
          </span>
        </div>
      ))}
    </div>
  );
}

export interface TrayRowItem {
  key: string;
  name: string;
  lead?: ReactNode;
  /** Trailing state, sans, always. */
  state?: string;
  /** A trailing link-register control with its own focus stop (ROUND2 §5). */
  action?: { label: string; onSelect: () => void };
}

/** The product's inline link: underline on hover and focus, ink on hover. */
const rowLink =
  "flex-none rounded-xs border-0 bg-transparent px-1 font-sans text-sm text-n-70 underline-offset-[0.22em] transition-colors duration-150 ease-out hover:duration-100 hover:text-n-95 hover:underline focus-visible:underline";

interface RowListProps {
  items: TrayRowItem[];
  value?: string | null;
  onChange?: (key: string) => void;
}

/** Icon + name + trailing state; selected is the neutral pressed register. */
export function RowList({ items, value, onChange }: RowListProps) {
  const selectable = value !== undefined;
  return (
    <div className="flex flex-col gap-0.5">
      {items.map((it) =>
        it.action ? (
          <div
            key={it.key}
            className="group flex min-h-8 items-center gap-2 rounded-sm px-2 transition-[background-color] duration-150 ease-out hover:duration-100 hover:bg-n-05"
          >
            <button
              type="button"
              className={cn(
                "flex min-h-8 min-w-0 flex-1 items-center gap-2 rounded-sm border-0 bg-transparent text-left text-base font-normal text-n-90 group-hover:text-n-95",
                focusInset,
              )}
              onClick={() => onChange?.(it.key)}
            >
              <span
                className="flex size-5 flex-none items-center justify-center text-n-70 transition-colors group-hover:text-n-95 [&>svg]:block"
                aria-hidden="true"
              >
                {it.lead}
              </span>
              <span className="min-w-0 flex-1 truncate">{it.name}</span>
            </button>
            <button
              type="button"
              className={cn(rowLink, focusInset)}
              aria-label={`${it.action.label} ${it.name}`}
              onClick={it.action.onSelect}
            >
              {it.action.label}
            </button>
          </div>
        ) : (
          <button
            key={it.key}
            type="button"
            className={cn(
              "group flex min-h-8 items-center gap-2 rounded-sm border-0 bg-transparent px-2 text-left text-base font-normal text-n-90",
              "transition-[background-color,color,box-shadow,opacity] duration-150 ease-out hover:duration-100 hover:bg-n-05 hover:text-n-95 active:opacity-92",
              "aria-pressed:bg-n-10 aria-pressed:text-n-95 aria-pressed:shadow-[inset_0_0_0_1px_var(--n-20)] aria-pressed:hover:shadow-[inset_0_0_0_1px_var(--n-30)]",
              focusInset,
            )}
            aria-pressed={selectable ? it.key === value : undefined}
            onClick={() => onChange?.(it.key)}
          >
            <span
              className="flex size-5 flex-none items-center justify-center text-n-70 transition-colors group-hover:text-n-95 [&>svg]:block"
              aria-hidden="true"
            >
              {it.lead}
            </span>
            <span className="min-w-0 flex-1 truncate">{it.name}</span>
            {it.state ? (
              <span className="flex-none text-sm text-n-50">{it.state}</span>
            ) : null}
          </button>
        ),
      )}
    </div>
  );
}

export interface TrayAppItem {
  key: string;
  name: string;
  short?: string;
  lead: ReactNode;
}

interface AppGridProps {
  items: TrayAppItem[];
  value?: string | null;
  onChange?: (key: string) => void;
}

/** A logo found by recognition: the bar's 40px tile holding a 24px mark, the
 * name a caption under it. Selection is neutral — a source is a place, not a
 * setting. */
export function AppGrid({ items, value, onChange }: AppGridProps) {
  return (
    <div className="grid grid-cols-[repeat(auto-fill,minmax(calc(var(--size-10)+var(--space-4)),1fr))] gap-x-2 gap-y-3">
      {items.map((it) => (
        <button
          key={it.key}
          type="button"
          className={cn(
            "group flex min-w-0 cursor-pointer flex-col items-center gap-1 border-0 bg-transparent p-0 active:opacity-92",
            focusInset,
          )}
          aria-pressed={it.key === value}
          aria-label={it.name}
          title={it.name}
          onClick={() => onChange?.(it.key)}
        >
          <span
            className="flex size-10 items-center justify-center rounded-lg bg-transparent transition-colors duration-150 group-hover:duration-100 group-hover:bg-n-10 group-aria-pressed:bg-n-10 group-aria-pressed:group-hover:bg-n-20 [&>*]:size-6"
            aria-hidden="true"
          >
            {it.lead}
          </span>
          <span className="block w-full min-w-0 truncate text-center text-xs leading-5 font-normal text-n-70 transition-colors group-hover:text-n-95 group-aria-pressed:font-medium group-aria-pressed:text-n-95">
            {it.short || it.name}
          </span>
        </button>
      ))}
    </div>
  );
}

export interface TrayTab {
  key: string;
  name: string;
  /** Icon-only tab; the name survives as tooltip and accessible name. */
  node?: ReactNode;
  body: ReactNode;
}

interface TrayTabsProps {
  tabs: (TrayTab & { id: string; panelId: string })[];
  value: string;
  onChange: (key: string) => void;
}

/** A strip of equal chips; the active one wears the group's own material.
 * Activation follows focus with a roving tabindex. */
export function TrayTabs({ tabs, value, onChange }: TrayTabsProps) {
  const btns = useRef<Record<string, HTMLButtonElement | null>>({});
  const chase = useRef<string | null>(null);

  useEffect(() => {
    const key = chase.current;
    if (key == null) return;
    chase.current = null;
    btns.current[key]?.focus();
  });

  const move = (delta: number, absolute?: number) => {
    const i = tabs.findIndex((t) => t.key === value);
    const next =
      absolute !== undefined
        ? absolute
        : Math.min(tabs.length - 1, Math.max(0, i + delta));
    const target = tabs[next];
    if (!target || target.key === value) return;
    chase.current = target.key;
    onChange(target.key);
  };

  return (
    <div
      role="tablist"
      className="-mx-2 flex min-w-0 flex-auto items-stretch gap-0.5 self-stretch overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      onKeyDown={(e) => {
        if (e.key === "ArrowRight") {
          e.preventDefault();
          move(1);
        } else if (e.key === "ArrowLeft") {
          e.preventDefault();
          move(-1);
        } else if (e.key === "Home") {
          e.preventDefault();
          move(0, 0);
        } else if (e.key === "End") {
          e.preventDefault();
          move(0, tabs.length - 1);
        }
      }}
    >
      {tabs.map((t) => {
        const on = t.key === value;
        return (
          <button
            key={t.key}
            id={t.id}
            ref={(el) => {
              btns.current[t.key] = el;
            }}
            type="button"
            role="tab"
            aria-selected={on}
            aria-controls={t.panelId}
            aria-label={t.node ? t.name : undefined}
            title={t.node ? t.name : undefined}
            tabIndex={on ? 0 : -1}
            className={cn(
              "relative inline-flex min-h-10 min-w-0 flex-1 cursor-pointer items-center justify-center self-stretch rounded-md border-0 bg-transparent px-2 font-sans text-base leading-none font-[450] tracking-normal whitespace-nowrap text-n-60",
              "transition-[background-color,color,opacity] duration-150 ease-out hover:duration-100 hover:bg-[color-mix(in_oklab,var(--n-05)_55%,transparent)] hover:text-n-80 active:opacity-92",
              "aria-selected:bg-n-05 aria-selected:font-medium aria-selected:text-n-95",
              "first:rounded-tl-xl last:rounded-tr-xl",
              focusInset,
            )}
            onClick={() => onChange(t.key)}
          >
            {t.node ?? t.name}
          </button>
        );
      })}
    </div>
  );
}

interface TabbedTrayProps {
  tabs: TrayTab[];
  /** The deck's standing first row; a function sees the active tab. */
  deckTop?: ReactNode | ((key: string) => ReactNode);
  foot?: ReactNode;
}

/** The tabbed shell: a pinned strip over one scrolling panel; the scroll
 * resets per tab, imperatively, so a field above the panel keeps focus. */
export function TabbedTray({ tabs, deckTop, foot }: TabbedTrayProps) {
  const base = useId();
  const [active, setActive] = useState(tabs[0]?.key);
  const strip = tabs.map((t) => ({
    ...t,
    id: `${base}-t-${t.key}`,
    panelId: `${base}-p-${t.key}`,
  }));
  const current = strip.find((t) => t.key === active) ?? strip[0];
  const deckRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (deckRef.current) deckRef.current.scrollTop = 0;
  }, [current?.key]);
  if (!current) return null;
  return (
    <Tray>
      <TrayHead>
        <TrayTabs tabs={strip} value={current.key} onChange={setActive} />
      </TrayHead>
      <TrayDeck deckRef={deckRef}>
        {typeof deckTop === "function" ? deckTop(current.key) : deckTop}
        <div
          key={current.key}
          id={current.panelId}
          role="tabpanel"
          aria-labelledby={current.id}
          className="contents"
        >
          {current.body}
        </div>
      </TrayDeck>
      {foot}
    </Tray>
  );
}

interface TraySearchProps {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  label: string;
}

/** A field is a recess: the panel's ground with an n-20 hairline that
 * deepens on hover and takes rust on focus. */
export function TraySearch({
  value,
  onChange,
  placeholder,
  label,
}: TraySearchProps) {
  return (
    <span className="relative block min-w-0 flex-auto">
      <Search
        className="pointer-events-none absolute top-1/2 left-2 -translate-y-1/2 text-n-50"
        size={CANVAS_MENU_GLYPH}
        strokeWidth={CANVAS_GLYPH_STROKE}
        aria-hidden="true"
      />
      <input
        type="text"
        className={cn(
          "h-full min-h-10 w-full min-w-0 rounded-md border border-n-20 bg-n-00 pr-3 pl-8 font-sans text-base text-n-95 placeholder:text-n-60 dark:bg-n-20",
          "transition-[border-color] duration-150 ease-out hover:duration-100 hover:border-n-30",
          "focus-visible:border-rust",
          focusInset,
        )}
        value={value}
        placeholder={placeholder}
        aria-label={label}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => e.stopPropagation()}
      />
    </span>
  );
}

interface TrayFindProps extends TraySearchProps {
  addLabel: string;
  onAdd: () => void;
}

/** Find-and-add: the left rail's own shape — a square add tile, then the
 * field taking the rest. Icon-only: the field beside it names the domain. */
export function TrayFind({ addLabel, onAdd, ...search }: TrayFindProps) {
  return (
    <div className="flex min-w-0 flex-auto items-stretch gap-1">
      <button
        type="button"
        className={cn(
          "inline-flex size-10 flex-none cursor-pointer items-center justify-center rounded-lg border-0 bg-n-00 p-0 text-n-70 dark:bg-n-20",
          "transition-[background-color,color,opacity] duration-150 ease-out hover:duration-100 hover:bg-n-10 hover:text-n-95 active:opacity-92",
          focusInset,
        )}
        aria-label={addLabel}
        title={addLabel}
        onClick={onAdd}
      >
        <Plus
          size={CANVAS_TILE_GLYPH}
          strokeWidth={CANVAS_GLYPH_STROKE}
          aria-hidden="true"
        />
      </button>
      <TraySearch {...search} />
    </div>
  );
}

/** A filtered-out band says so in words rather than collapsing. */
export interface TrayEmojiItem {
  key: string;
  char: string;
  name: string;
}

interface EmojiGridProps {
  items: TrayEmojiItem[];
  value?: string | null;
  onPick: (item: TrayEmojiItem) => void;
  /** Before the click: a press that should not move focus. */
}

/**
 * Emoji are content, not iconography (the sheet's `.xtc-emoji`): a 48px
 * cell, the character large in the platform's own colour font, the cell a
 * hit target and a hover ground and nothing else. Seven per row.
 */
export function EmojiGrid({ items, value = null, onPick }: EmojiGridProps) {
  return (
    <div className="grid grid-cols-[repeat(auto-fill,minmax(var(--size-12),1fr))] gap-1">
      {items.map((it) => (
        <button
          key={it.key}
          type="button"
          className={cn(
            "flex aspect-square min-w-0 items-center justify-center rounded-lg border-0 bg-transparent p-0 text-2xl leading-none",
            "transition-[background-color,opacity] duration-150 ease-out hover:duration-100 hover:bg-n-05 active:duration-0 active:opacity-92",
            "aria-pressed:bg-n-10",
            focusInset,
          )}
          style={{ fontFamily: "var(--xtc-face-emoji)" }}
          aria-label={it.name}
          aria-pressed={value !== null ? it.key === value : undefined}
          title={it.name}
          onClick={() => onPick(it)}
        >
          {it.char}
        </button>
      ))}
    </div>
  );
}

export interface TrayStickerItem {
  key: string;
  name: string;
  title?: string;
  art: ReactNode;
}

interface StickerGridProps {
  items: TrayStickerItem[];
  value?: string | null;
  onChange?: (key: string) => void;
  onDragStart?: (
    item: TrayStickerItem,
    e: React.DragEvent<HTMLElement>,
  ) => void;
}

/**
 * Drawn stickers (the sheet's `.xtc-stickers`): a 3:2 plate per sticker,
 * three per row, the art fluid inside it, no frame and no ground of its
 * own — space separates one badge from the next. The armed plate reports
 * pressed (ROUND2 §3).
 */
export function StickerGrid({
  items,
  value = null,
  onChange,
  onDragStart,
}: StickerGridProps) {
  return (
    <div className="grid grid-cols-[repeat(auto-fill,minmax(calc(var(--size-12)*2),1fr))] items-start gap-3">
      {items.map((it) => (
        <button
          key={it.key}
          type="button"
          draggable={onDragStart ? true : undefined}
          className={cn(
            "block min-w-0 rounded-lg border-0 bg-transparent p-0 text-left",
            "transition-[background-color,opacity] duration-150 ease-out hover:duration-100 hover:bg-n-05 active:duration-0 active:opacity-92",
            "aria-pressed:bg-n-10",
            focusInset,
          )}
          aria-label={it.name}
          aria-pressed={value !== null ? it.key === value : undefined}
          title={it.title ?? it.name}
          onClick={() => onChange?.(it.key)}
          onDragStart={onDragStart ? (e) => onDragStart(it, e) : undefined}
        >
          <span className="block aspect-[3/2] w-full [&>*]:h-full [&>*]:w-full">
            {it.art}
          </span>
        </button>
      ))}
    </div>
  );
}

export function TrayEmpty({ children }: { children: ReactNode }) {
  return <p className="m-0 py-1 text-sm text-n-50">{children}</p>;
}
